import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { RealTimeProvider } from './contexts/RealTimeContext';
import RouteTracker from './components/RouteTracker';
import ScrollToTop from './components/ScrollToTop';

// Layouts
const MainLayout = lazy(() => import('./layouts/MainLayout'));
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));

// Public Pages
const Home = lazy(() => import('./pages/Home'));
const Article = lazy(() => import('./pages/Article'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const Search = lazy(() => import('./pages/Search'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const NotFound = lazy(() => import('./pages/NotFound'));
const SetupAdmin = lazy(() => import('./pages/SetupAdmin'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

// Admin Pages
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const ArticleList = lazy(() => import('./pages/admin/ArticleList'));
const ArticleEditor = lazy(() => import('./pages/admin/ArticleEditor'));
const CategoryManager = lazy(() => import('./pages/admin/CategoryManager'));
const UserManager = lazy(() => import('./pages/admin/UserManager'));
const MediaLibrary = lazy(() => import('./pages/admin/MediaLibrary'));
const SubscriberManager = lazy(() => import('./pages/admin/SubscriberManager'));
const CommentManager = lazy(() => import('./pages/admin/CommentManager'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const ActivityLogs = lazy(() => import('./pages/admin/ActivityLogs'));
const ContactManager = lazy(() => import('./pages/admin/ContactManager'));

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-600"></div>
      <p className="text-gray-500 font-medium animate-pulse font-serif italic">Loading excellence...</p>
    </div>
  </div>
);

function App() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <RealTimeProvider>
        <RouteTracker />
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<MainLayout />}>
            <Route index element={<Home />} />
            <Route path="article/:slug" element={<Article />} />
            <Route path="category/:slug" element={<CategoryPage />} />
            <Route path="search" element={<Search />} />
            <Route path="about" element={<About />} />
            <Route path="contact" element={<Contact />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-service" element={<TermsOfService />} />
            <Route path="cookie-policy" element={<CookiePolicy />} />
            <Route path="disclaimer" element={<Disclaimer />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/setup" element={<SetupAdmin />} />
          <Route path="/admin/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="articles" element={<ArticleList />} />
            <Route path="articles/new" element={<ArticleEditor />} />
            <Route path="articles/edit/:id" element={<ArticleEditor />} />
            <Route path="categories" element={<CategoryManager />} />
            <Route path="users" element={<UserManager />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="subscribers" element={<SubscriberManager />} />
            <Route path="comments" element={<CommentManager />} />
            <Route path="contact-messages" element={<ContactManager />} />
            <Route path="settings" element={<Settings />} />
            <Route path="activity" element={<ActivityLogs />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </RealTimeProvider>
    </Suspense>
  );
}

export default App;
